/**
 * ext-list-columns.tsx — wspólna warstwa „pola dodatkowe (ext) jako kolumny listy".
 *
 * Wyekstrahowane z `pages/Contractors.tsx` (Krok 1 epiki picker+ext) przy adopcji przez
 * kolejne kartoteki (firmy/pracownicy/towary) — jedno źródło prawdy zamiast 4 kopii.
 *
 * Zawiera:
 *  - `SystemModuleFieldDef` — kształt pola z `GET /api/system-modules/{slug}/fields`
 *    (tylko pola używane do generowania i renderowania kolumn ext).
 *  - `useSystemModuleFields(slug)` — katalog pól modułu (współdzielony cache po slugu).
 *  - `renderExtFieldValue(value, field, t)` — generyczny render wartości wg `fieldType`.
 *  - `buildExtColumns(fields, t)` — kolumny `ColumnDefinition` dla pól `isCore=false`.
 *  - `computeVisibleColumnParams(...)` — zbiór slugów aktualnie widocznych kolumn dla
 *    `?columns=` (core-slug przez mapę, ext-slug wprost), z opcją „zawsze dołącz"
 *    (zależność logiki strony), regułą towarzysza (np. nazwa → krótka nazwa) i slugami pól
 *    kalendarza układu (dołączanymi niezależnie od widoczności kolumn).
 *  - `buildCategoryColumn(opts)` — kolumna „Kategoria" (oś `record_categories`, ADR-0093b),
 *    JEDNO źródło prawdy dla wszystkich kartotek/list na silniku list, które mają
 *    `category_id`: Kontrahenci (pilot), 7 klas dokumentów, Pracownicy. Mirror
 *    `buildCompanyColumn` — kolumna RĘCZNA (accessor `categoryName`, projekcja nazwy z LEFT
 *    JOIN w deskryptorze), NIE z katalogu pól (`category_id` ma `showInList: false` wszędzie,
 *    bo lista pokazuje nazwę, nie uuid).
 */

import type React from 'react';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { CalendarConfig } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import type {
  ColumnDefinition,
  ColumnFilters,
  ColumnHeaderFilter,
  ColumnHeaderFilters,
} from '@/components/configurable-table';
import type {
  ColumnFilterCategory,
  ColumnHeaderFilterConfig,
} from '@/components/configurable-table/types';
import { api } from '@/lib/api';
import { slugToCamel } from '@/lib/slug-to-camel';

/**
 * Pole modułu systemowego z `GET /api/system-modules/{slug}/fields` (odpowiedź zwraca
 * pełny wiersz `system_module_fields` — tu tylko pola potrzebne do wygenerowania kolumn
 * ext i ich renderowania).
 */
export interface SystemModuleFieldDef {
  id: string;
  slug: string;
  name: string;
  fieldType: string;
  isCore: boolean;
  showInList: boolean;
  sortOrder: number;
  isFilterable: boolean;
  /** Pole tylko do odczytu (np. pola integracji Symfonia) — panel go nie zapisuje. */
  readOnly?: boolean;
  options: Array<{ value: string; label: string; color?: string | null }> | null;
  /**
   * Kod słownika, z którego pole `select` bierze wartości (np. `purchase_invoice_status`) — wtedy
   * `options` jest puste, a pozycje trzeba wczytać osobno (`useListFilterOptions`). Pole zwraca
   * serwer (`GET /system-modules/:slug/fields`); `undefined`/`null` = pole bez słownika.
   */
  dictionaryCode?: string | null;
}

/**
 * Jedna pozycja listy wyboru filtra w wierszu filtrów (kształt `ColumnHeaderFilterConfig.options`):
 * `value` to DOKŁADNIE to, co serwer trzyma w kolumnie (kod pozycji słownika / kod enuma), `label` to
 * tekst dla człowieka.
 */
export type ListFilterOption = NonNullable<ColumnHeaderFilterConfig['options']>[number];

/** Opcje filtra pól `select` rdzenia wg sluga pola — patrz `buildCoreColumns({ selectOptions })`. */
export type CoreSelectFilterOptions = Readonly<Record<string, ReadonlyArray<ListFilterOption>>>;

/**
 * Mapa `fieldType` → `ColumnFilterCategory` (popup filtra w nagłówku kolumny).
 * `multiselect` i `reference` celowo → `'none'` (brak filtra w tym slice — patrz
 * komentarz przy `buildExtColumns`).
 *
 * Eksportowana (poza ext) — `RecordTablePicker` (`@/components/ui/RecordTablePicker.tsx`)
 * reużywa tę samą regułę dla kolumn CORE (jedno źródło prawdy typ→kategoria, core i ext).
 */
export function mapExtFieldTypeToFilterCategory(fieldType: string): ColumnFilterCategory {
  switch (fieldType) {
    case 'varchar':
    case 'text':
    case 'email':
    case 'url':
      return 'text';
    case 'integer':
    case 'numeric':
      return 'number';
    case 'date':
    case 'timestamp':
      return 'date';
    case 'select':
      return 'select';
    case 'boolean':
      return 'boolean';
    case 'multiselect':
    case 'reference':
    default:
      return 'none';
  }
}

/**
 * Czy typ pola z katalogu to data/czas — `date` albo `timestamp` (jedyne typy daty w
 * `SYSTEM_EXT_FIELD_TYPES`, `packages/shared/src/field-types.ts`, używane też przez pola
 * rdzenne kartotek w `seed-system-modules.ts`). Reguła jest tożsama z kategorią filtra
 * `date` powyżej: to na takim filtrze silnik list zawęża listę do miesiąca kalendarza
 * (`mode: 'range'`), więc tylko te pola nadają się na oś czasu kalendarza. `undefined`
 * (typ nieznany w danym kontekście) → `false`.
 */
export function isDateFieldType(fieldType: string | undefined): boolean {
  return fieldType !== undefined && mapExtFieldTypeToFilterCategory(fieldType) === 'date';
}

/**
 * Katalog pól modułu (core + ext) — źródło kolumn dodatkowych generowanych dynamicznie
 * i etykiet do `?columns=`. Ten sam `queryKey` na każdej stronie czytającej dany slug
 * (współdzielony cache; np. lista i picker tego samego modułu).
 */
export function useSystemModuleFields(slug: string) {
  return useQuery({
    queryKey: ['system-module-fields', slug],
    queryFn: async () => {
      const res = await api.get<{ fields: SystemModuleFieldDef[] }>(
        `/system-modules/${slug}/fields`,
      );
      return res.data.fields ?? [];
    },
    staleTime: 60_000,
  });
}

/**
 * Stan błędu katalogu pól. Katalog jest TWARDĄ zależnością listy data-driven:
 * bez niego lista nie ma kolumn rdzennych, a projekcja `?columns=` jest niepełna —
 * ciche renderowanie pustej tabeli maskowałoby awarię (reguła: raise errors
 * explicitly). Strony renderują ten komponent ZAMIAST tabeli, gdy
 * `useSystemModuleFields(...).isError`. Strażnik CELOWO obejmuje tylko tabelę:
 * nagłówek strony, filtry i pasek narzędzi zostają aktywne (zapytanie listy i tak
 * jest po stronie serwera ograniczone whitelistą kolumn — brak ryzyka danych).
 */
export function FieldCatalogErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="field-catalog-error"
      className="rounded border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300"
    >
      <p>{t('common.lists.fieldCatalogError')}</p>
      <Button variant="secondary" size="sm" className="mt-2" onClick={onRetry}>
        {t('common.lists.fieldCatalogRetry')}
      </Button>
    </div>
  );
}

/**
 * Etykiety pól typu `reference` rozwiązane przez backend — grupowane po slugu pola,
 * potem po id wartości. Doklejane jako SIBLING obok wierszy listy (nie w `row.ext`)
 * gdy projekcja `?columns=` obejmuje przynajmniej jedno pole `reference` (kontrakt
 * `extRefLabels` w odpowiedzi list, np. `GET /api/contractors?columns=`). Wartość
 * pod id:
 *  - `string` — etykieta rozwiązana (cel istnieje, aktywny).
 *  - `null` — cel ISTNIEJE, ale jest miękko skasowany (jawny sentinel backendu) —
 *    renderer pokazuje `t('ui.extRef.deletedTarget')`.
 *  - klucz NIEOBECNY — cel nierozwiązywalny (brak dostępu / nieznane id) — backend
 *    go po prostu pomija; renderer pokazuje surowe id.
 */
export type ExtRefLabels = Record<string, Record<string, string | null>>;

/**
 * Stabilny getter najświeższej mapy `extRefLabels`, do przekazania w `buildExtColumns`
 * bez wchodzenia do zależności `useMemo` budującego kolumny. Kolumny (`extColumns`) są
 * memoizowane na katalogu pól (`fieldsQ.data`) i `t` — gdyby zamiast tego zależały
 * bezpośrednio od mapy etykiet, każda odpowiedź listy (nowa referencja obiektu z
 * `useQuery`) wymuszałaby przebudowę `extColumns` → `columns` → `visibleColumnParams`
 * (który wchodzi do `queryKey` listy — niepotrzebny recompute na każdym fetchu).
 * Zwraca `[getRefLabels, setRefLabels]`: `getRefLabels` ma STAŁĄ tożsamość (pusta
 * tablica zależności `useCallback`) — czyta zawsze aktualną wartość przez ref,
 * wywoływana dopiero w `renderCell` przy renderze komórki. `setRefLabels` aktualizuje
 * ref — wołane jako zwykłe wywołanie funkcji w ciele komponentu (nie hook), więc może
 * wystąpić PO `useQuery` listy (kolejność odwrotna do `extColumns`/`getRefLabels`,
 * które muszą być zdefiniowane PRZED `queryKey`/`listQ` — patrz komentarz w
 * `Contractors.tsx` przy `extColumns`).
 *
 * INWARIANT: zapis do refa jest bezpieczny bez powiadamiania Reacta o zmianie
 * (`setRefLabels` nie wywołuje re-renderu) WYŁĄCZNIE dlatego, że `ConfigurableTable`
 * nigdy nie memoizuje komórek — `renderCell` woła się na nowo przy KAŻDYM renderze
 * tabeli, więc `getRefLabels()` zawsze czyta aktualną wartość z refa. Jeśli
 * `ConfigurableTable` kiedyś zacznie memoizować wiersze/komórki (np. `React.memo`
 * na komponencie komórki), ten inwariant pęka — zrewiduj.
 */
export function useExtRefLabelsGetter(): [
  getRefLabels: () => ExtRefLabels | undefined,
  setRefLabels: (labels: ExtRefLabels | undefined) => void,
] {
  const ref = useRef<ExtRefLabels | undefined>(undefined);
  const getRefLabels = useCallback(() => ref.current, []);
  // Guard równością referencji — idiom „store info from previous render" (React docs):
  // zapis TYLKO gdy wartość faktycznie się zmieniła, żeby powtórne wywołanie z tym
  // samym obiektem (ta sama referencja `useQuery.data` między renderami bez refetchu)
  // nie nadpisywało refa bez potrzeby.
  const setRefLabels = useCallback((labels: ExtRefLabels | undefined) => {
    if (ref.current !== labels) ref.current = labels;
  }, []);
  return [getRefLabels, setRefLabels];
}

/**
 * Komórka pola typu boolean — checkbox tylko-do-odczytu (`Checkbox`, biblioteka UI —
 * §4.12 zabrania surowego `<input>`), wyśrodkowany w komórce, zamiast dawnego renderu
 * tekstowego (ikony Check/X). Współdzielona przez DWIE ścieżki kolumn: ext (`renderExtFieldValue`
 * niżej) i core (`RecordTablePicker` — pola core typu `boolean` z katalogu kartoteki/klasy,
 * gdzie generyczny `accessor` bez tego helpera drukował surowy tekst `true`/`false`).
 *
 * `null`/`undefined` → myślnik, zgodnie z resztą typów (dla ext ten guard jest już wyżej w
 * `renderExtFieldValue` — funkcja i tak sama go powtarza, żeby callsite core (bez
 * odpowiednika tego guardu) dostał to samo zachowanie bez duplikowania logiki).
 *
 * `disabled` (nie tylko `readOnly` — HTML ignoruje `readonly` na checkboxach) blokuje
 * interakcję; `readOnly` dodatkowo cisza ostrzeżenie Reacta o kontrolowanym polu bez
 * `onChange`. `aria-checked` jawnie obok natywnego `checked` (żądanie właściciela —
 * redundantne dla natywnego checkboxa, ale nieszkodliwe).
 */
export function renderBooleanCell(value: unknown, t: (key: string) => string): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-text-tertiary">—</span>;
  }
  const checked = value === true;
  return (
    <div className="flex justify-center">
      <Checkbox
        checked={checked}
        disabled
        readOnly
        aria-checked={checked}
        aria-label={t(checked ? 'common.yes' : 'common.no')}
      />
    </div>
  );
}

/**
 * Rozwiązuje POJEDYNCZE id odwołania (`reference`) na etykietę — jedna reguła
 * współdzielona przez `renderExtFieldValue` (branch `reference` niżej, wielowartościowa:
 * mapuje TABLICĘ id przez tę funkcję per element) i `renderCoreReferenceCell` (core-
 * referencje, zawsze jednowartościowe — FK UUID). Brak wpisu w mapie = nierozwiązywalne
 * (surowe id jako fallback); `null` = cel ISTNIEJE, ale jest skasowany (sentinel
 * `ui.extRef.deletedTarget`) — patrz kontrakt `ExtRefLabels` wyżej.
 *
 * `unresolvedLabel` — OPCJONALNY tekst zastępczy dla odwołania nierozwiązywalnego (brak klucza w
 * mapie): zamiast surowego id pokazujemy go. Dla ekranów, gdzie surowy identyfikator nic nie mówi
 * użytkownikowi (sekcje kokpitu decyzji: „(brak dostępu)"). Domyślnie wyłączony — zwykłe listy
 * kartotek zachowują surowe id (zatwierdzony kontrakt opiekuna/kategorii/ext).
 */
export function resolveReferenceLabel(
  idStr: string,
  refLabelsForField: Record<string, string | null> | undefined,
  t: (key: string) => string,
  unresolvedLabel?: string,
): string {
  // `Object.hasOwn` (nie `in`) — `in` przechodzi łańcuch prototypów, więc id literalnie
  // równe nazwie odziedziczonej metody (`toString`, `constructor`, `hasOwnProperty`, ...)
  // fałszywie „znalazłoby" wpis (funkcję z Object.prototype) zamiast trafić w fallback.
  if (!refLabelsForField || !Object.hasOwn(refLabelsForField, idStr)) {
    return unresolvedLabel ?? idStr;
  }
  const resolved = refLabelsForField[idStr];
  // `noUncheckedIndexedAccess` typuje indeksowanie jako `string | null | undefined` mimo
  // `Object.hasOwn` powyżej (TS nie zawęża po runtime-checku obecności klucza) —
  // `undefined` faktycznie nieosiągalny tutaj, ale fallback do `idStr` zamiast asercji
  // `!` jest bezpieczny identycznie (ten sam surowy-id fallback co reszta funkcji).
  if (resolved === null) return t('ui.extRef.deletedTarget');
  return resolved ?? idStr;
}

/**
 * Render wartości pola dodatkowego (ext) wg `fieldType` — generyczny, współdzielony
 * przez wszystkie kolumny ext generowane z katalogu pól. `select`/`multiselect`
 * mapują zapisaną wartość na `label` z `field.options` (fallback: surowa wartość).
 * `reference` mapuje id (lub tablicę id — pole wielokrotnego odwołania) na etykietę
 * przez `resolveReferenceLabel` (patrz wyżej); `unresolvedRefLabel` — opcjonalny tekst
 * zastępczy dla odwołań nierozwiązywalnych (domyślnie surowe id).
 */
export function renderExtFieldValue(
  value: unknown,
  field: SystemModuleFieldDef,
  t: (key: string) => string,
  refLabelsForField?: Record<string, string | null>,
  unresolvedRefLabel?: string,
): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return <span className="text-text-tertiary">—</span>;
  }
  switch (field.fieldType) {
    case 'boolean':
      return renderBooleanCell(value, t);
    case 'date': {
      // Data bez czasu (np. '2026-07-30'). `new Date('YYYY-MM-DD')` parsuje jako północ
      // UTC i przy `toLocaleDateString` w strefie UTC-ujemnej pokazałby dzień wcześniej —
      // budujemy więc datę z komponentów w czasie LOKALNYM.
      const s = String(value);
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
      if (!m) return <span>{s}</span>;
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return <span>{Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('pl-PL')}</span>;
    }
    case 'timestamp': {
      // Timestamp niesie czas/strefę — `new Date` parsuje poprawnie; pokazujemy datę + godzinę.
      const d = new Date(String(value));
      return <span>{Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('pl-PL')}</span>;
    }
    case 'select':
    case 'multiselect': {
      const rawValues = Array.isArray(value) ? value : [value];
      const options = field.options ?? [];
      const labels = rawValues.map((v) => {
        const match = options.find((opt) => String(opt.value) === String(v));
        return match?.label ?? String(v);
      });
      return <span>{labels.join(', ')}</span>;
    }
    case 'reference': {
      // Pole wielokrotnego odwołania — wartość jest tablicą id; pojedyncze
      // odwołanie — jedno id. Ten sam kształt co `select`/`multiselect` wyżej.
      const ids = Array.isArray(value) ? value : [value];
      const labels = ids.map((raw) =>
        resolveReferenceLabel(String(raw), refLabelsForField, t, unresolvedRefLabel),
      );
      return <span>{labels.join(', ')}</span>;
    }
    default:
      return <span>{String(value)}</span>;
  }
}

/**
 * Komórka core-pola typu `reference` (FK UUID JEDNOWARTOŚCIOWEJ — np.
 * `owner_user_id`/`assigned_user_id`/`company_id`/`primary_category_id`, jedyne
 * core-referencje z resolverem etykiety w `extRefLabels` na trasach list/klas/pickera).
 * W odróżnieniu od `renderExtFieldValue`'s branch `reference` (pola ext, które MOGĄ być
 * wielokrotne) ten renderer jest ZAWSZE jednowartościowy — core FK nie są tablicami —
 * ale reużywa dokładnie tę samą regułę rozwiązywania etykiety (`resolveReferenceLabel`).
 * Core-referencje SPOZA tego zbioru (np. `corrected_invoice_id`, `vendor_bank_account_id`)
 * nie mają resolvera i zostają wykluczone z kolumn wcześniej (patrz `RecordTablePicker.tsx`) —
 * ten renderer nie jest dla nich wołany.
 *
 * Curry: `slug`/`getRefLabels`/`t`/`fallback` są stałe per kolumna (ustalone przy budowie
 * kolumn), `value` zmienia się per komórka — zwracana funkcja pasuje wprost pod
 * `ColumnDefinition.renderCell`. `getRefLabels` wołany PRZY KAŻDYM renderze komórki (nie
 * przy budowie kolumn, mirror `buildExtColumns`), więc kolumna zawsze czyta najświeższą mapę.
 *
 * `fallback` — co pokazać gdy id jest NIEROZWIĄZYWALNE (klucz nieobecny w `extRefLabels`,
 * różne od sentinela „skasowany" — ten idzie zawsze przez `resolveReferenceLabel` niezależnie
 * od `fallback`):
 *  - `'id'` (domyślne) — surowe id, zatwierdzony kontrakt dla opiekuna/kategorii/ext.
 *  - `'dash'` — myślnik „—", jak stare zachowanie lokalnego lookupu (`companiesById` itp.)
 *    sprzed tego resolvera. Używane dla kolumny „firma" (`company_id`) — user, który widzi
 *    dokument/rekord (`documents`/`hr.employees`.view=all) ale NIE ma `companies.view`, nie
 *    dostaje wpisu w `extRefLabels` dla tej firmy (nierozwiązywalne) — pokazanie surowego UUID
 *    byłoby regresją względem starego lookupu, który po prostu nie znajdował firmy na liście
 *    i renderował myślnik.
 *
 * `unresolvedLabel` — opcjonalny tekst zastępczy dla fallbacku `'id'` (zamiast surowego id,
 * patrz `resolveReferenceLabel`); domyślnie wyłączony. Fallback `'dash'` zostaje myślnikiem —
 * też nie pokazuje surowego id.
 */
export function renderCoreReferenceCell(
  slug: string,
  getRefLabels: (() => ExtRefLabels | undefined) | undefined,
  t: (key: string) => string,
  fallback: 'id' | 'dash' = 'id',
  unresolvedLabel?: string,
): (value: unknown) => React.ReactNode {
  return (value: unknown) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-text-tertiary">—</span>;
    }
    const idStr = String(value);
    const refLabelsForField = getRefLabels?.()?.[slug];
    if (fallback === 'dash' && !(refLabelsForField && Object.hasOwn(refLabelsForField, idStr))) {
      return <span className="text-text-tertiary">—</span>;
    }
    return <span>{resolveReferenceLabel(idStr, refLabelsForField, t, unresolvedLabel)}</span>;
  };
}

/**
 * Kolumna „Firma" — jeden budowniczy dla kartotek z polem firmowym (Faza 0 fundamentu:
 * TYLKO ten builder, BEZ wpięcia na żadną stronę — adopcja przez 11 list jest osobnym
 * etapem). Kopiuje 1:1 kolumnę „firma" istniejącą dziś na listach dokumentów (patrz
 * np. `Contracts.tsx`, `id: 'company'`): sam `id`/`defaultWidth`/`sortable`/renderer.
 *
 * `id: 'company'` MUSI zostać stałe — zapisane layouty (`list_config.columns`) kluczują
 * kolumny po `id`; zmiana złamałaby istniejące układy userów.
 *
 * `renderCell` zawsze rozwiązuje etykietę pod slugiem `company_id` w `extRefLabels`
 * — NIEZALEŻNIE od `accessor`/`fieldSlug` przekazanych w `opts` — backend klucze
 * etykiet firm trzyma tam zawsze (kontrahenci mają FK `home_company_id`, ale backend
 * i tak publikuje etykietę pod `company_id`, patrz brief Fazy 0).
 *
 * `headerFilter` (record-select — searchable multiselect z auto-selectem po
 * wpisaniu tekstu, statyczne `options` z `companies` — BEZ fetchu `/distinct`, bo
 * firmy user i tak ma w `me.companies`) jest dołączany zawsze, gdy `companies`
 * jest niepuste — pusty `options` uruchomiłby fallback do `/distinct` (surowe UUID
 * zamiast nazw firm) — inwariant tego buildera. Filtr jest ZAWSZE widoczny i ZAWSZE
 * aktywny, niezależnie od globalnie wybranej firmy czy liczby firm usera — „global
 * company wins" jest egzekwowane WYŁĄCZNIE na poziomie zapytania (strona wysyła
 * jednocześnie `?companyId=` global i filtr kolumnowy, backend je ANDuje: global
 * zawęża zakres, filtr zawęża w jego obrębie, sprzeczny wybór usera daje pustą
 * listę) — NIE przez ukrywanie/wyłączanie tego kontrolki.
 *
 * `accessor`/`fieldSlug` mają niezależne domyślne wartości (`companyId`/`company_id`
 * — kontrakt dokumentów). Kontrahenci nadpiszą obiema na `homeCompanyId`/
 * `home_company_id` przy adopcji (poza zakresem tego pliku). `fieldSlug` trafia też na samą
 * definicję kolumny (`ColumnDefinition.fieldSlug`) — niezależnie od tego, czy `headerFilter`
 * powstał — żeby kalendarz listy znalazł kolumnę po slugu pola z katalogu także przy pustej liście
 * firm.
 *
 * `defaultVisible: true` jest ZAMIERZONE i celowo NIEparametryzowane — decyzja o
 * ujednoliceniu: kolumna „Firma" domyślnie widoczna na WSZYSTKICH listach adoptujących
 * (znosimy dawne auto-ukrywanie `scope.canPickAll` z 6 list dokumentów; Pracownicy już
 * mieli `true`). Użytkownik może ją ukryć ręcznie w ustawieniach kolumn (layout).
 */
export function buildCompanyColumn<Row>(opts: {
  companies: ReadonlyArray<{ id: string; name: string }>;
  getRefLabels: (() => ExtRefLabels | undefined) | undefined;
  t: (key: string) => string;
  accessor?: string;
  fieldSlug?: string;
  headerKey?: string;
}): ColumnDefinition<Row> {
  const {
    companies,
    getRefLabels,
    t,
    accessor = 'companyId',
    fieldSlug = 'company_id',
    headerKey = 'tenant.documents.columns.company',
  } = opts;
  return {
    id: 'company',
    accessor,
    header: t(headerKey),
    // Slug pola backendu NIEZALEŻNY od filtra (`headerFilter` poniżej bywa nieobecny przy pustej
    // liście firm) — po nim kalendarz listy dopasowuje pole zapisane jako slug z katalogu
    // (`company_id`) do kolumny o `id: 'company'`.
    fieldSlug,
    defaultVisible: true,
    defaultWidth: 200,
    sortable: false,
    // Kolumna referencyjna (uuid) — NIGDY nie ma generycznego pola „Szukaj…". Filtr firmy
    // to record-select (niżej), renderowany osobną ścieżką. Bez `filterable: false`, gdy
    // `headerFilter` jest pominięty (companies puste), wiersz filtra pokazałby martwy input
    // tekstowy piszący do `columnFilters.company`, który i tak nic nie zawęża.
    filterable: false,
    renderCell: renderCoreReferenceCell('company_id', getRefLabels, t, 'dash'),
    ...(companies.length > 0
      ? {
          headerFilter: {
            category: 'record-select' as const,
            fieldSlug,
            options: companies.map((c) => ({ value: c.id, label: c.name })),
          },
        }
      : {}),
  };
}

/**
 * Kolumna „Kategoria" — oś `record_categories` (ADR-0093b), WSPÓLNA dla każdej kartoteki/listy
 * NA SILNIKU LIST z `category_id`: Kontrahenci (pilot), 7 klas dokumentów (faktury/umowy/pisma/
 * handlowe/wewnętrzne/specjalne), Pracownicy. JEDNO źródło prawdy — zmiana tutaj jest widoczna na
 * wszystkich listach naraz, bez kopiowania bloku po stronach. (Kontakty kontrahentów mają
 * WŁASNĄ oś kategorii, ale NIE są kartoteką na silniku list — lista zagnieżdżona w zakładce
 * kontrahenta, rozwiązywanie nazwy po stronie klienta — więc nie korzystają z tego buildera.)
 *
 * Kolumna RĘCZNA (jak `buildCompanyColumn`), NIE z katalogu pól: `category_id` ma
 * `showInList: false` wszędzie (patrz `seed-system-modules.ts` / kartoteka-kategorie), bo
 * lista pokazuje NAZWĘ kategorii (`categoryName`, projekcja z LEFT JOIN do `record_categories`
 * w deskryptorze backendu — `kartotekaKey` filtruje wg właściwej kartoteki), nie surowe uuid.
 * Filtr jest warunkiem tekstowym po nazwie (`conditionOnly` — pomija /distinct, kolumna jest
 * liczona z joina, nie z tabeli głównej), zawsze obecny (bez gate'u filterable — parytet z
 * pilotem `Contractors.tsx`).
 *
 * `Row` niesparametryzowany celowo (jak `buildCompanyColumn`/`buildExtColumns`) — wymaga
 * tylko, żeby wiersz niósł pole `categoryName?: string | null` (każdy adopter go deklaruje).
 */
export function buildCategoryColumn<Row>(opts: {
  t: (key: string) => string;
  /**
   * Sortowalność serwerowa (mirror `resolveSortable` z `buildCoreColumns`) — `category` jest
   * już w whiteliście każdego adoptowanego deskryptora list, silnik go sortuje przez
   * `autoSortColumns`. Pominięte = `sortable: false`.
   */
  resolveSortable?: (slug: string) => boolean;
}): ColumnDefinition<Row> {
  const { t, resolveSortable } = opts;
  return {
    id: 'category',
    accessor: 'categoryName',
    header: t('tenant.recordCategories.picker.label'),
    defaultVisible: true,
    defaultWidth: 180,
    sortable: resolveSortable ? resolveSortable('category') : false,
    renderCell: (value: unknown) =>
      value == null || value === '' ? <span className="text-text-tertiary">—</span> : String(value),
    headerFilter: { category: 'text', fieldSlug: 'category', conditionOnly: true },
  };
}

/**
 * Core-referencje (FK UUID) Z RESOLVEREM ETYKIETY w `extRefLabels` — backend dokleja do
 * `extRefLabels` (sibling odpowiedzi listy, patrz `ExtRefLabels` wyżej) etykiety tych ze
 * slugów tego zbioru, które FAKTYCZNIE istnieją jako kolumna danej encji/klasy i zostały
 * zażądane przez `?columns=`. Core-referencje SPOZA tego zbioru (np. `corrected_invoice_id`,
 * `vendor_bank_account_id`, `vendor_id`) NIE mają resolvera — bez jawnego nadpisania (override)
 * kolumna wyrenderowałaby surowy UUID, więc `buildCoreColumns` je pomija (patrz niżej).
 *
 * Współdzielone przez `buildCoreColumns` (listy) i `RecordTablePicker` (picker-tabela) —
 * jedno źródło prawdy. Gdy backend rozszerzy `extRefLabels` o kolejne core-referencje,
 * dopisz slug tutaj.
 */
export const CORE_REFERENCE_SLUGS_WITH_LABEL = new Set<string>([
  'owner_user_id',
  'assigned_user_id',
  'company_id',
  'primary_category_id',
]);

/**
 * Slugi pól-referencji rdzennych renderowanych przez NADPISANIA konkretnych stron
 * (rejestry `CoreColumnOverride`, np. kompozytowa kolumna „Dostawca"/„Nabywca").
 * Paleta „Dodaj kolumnę" kreatora nie zna rejestrów stron, więc bez tej mapy brama
 * `isFieldListable` odsiałaby pole, które strona UMIE narysować — usunięcie takiej
 * kolumny w kreatorze byłoby nieodwracalne z UI. Strona dodająca nadpisanie pola
 * typu `reference` MUSI dopisać jego slug tutaj (typów nie-referencyjnych to nie
 * dotyczy — przechodzą bramę typu same); dryf łapie dev-strażnik w `buildCoreColumns`.
 */
export const PAGE_OVERRIDE_REFERENCE_SLUGS: Record<string, readonly string[]> = {
  purchase_invoices: ['vendor_id'],
  sale_invoices: ['vendor_id'],
  // Pracownicy: „Jednostka" (`org_unit_id`, F24 Pakiet 0, dawniej `department_id`) i
  // „Przełożony" (`manager_employee_id`) to referencje rdzenne BEZ resolvera etykiety
  // (`CORE_REFERENCE_SLUGS_WITH_LABEL` ich nie zna), rysowane nadpisaniem na stronie
  // (jednostka: nazwa z projekcji serwera `orgUnitName`; przełożony: z lokalnej mapy pracowników).
  // Wpis pozwala palecie „Dodaj kolumnę" je zaoferować — bez niego usunięcie tych kolumn w
  // Kreatorze Listy byłoby nieodwracalne z UI.
  employees: ['org_unit_id', 'manager_employee_id'],
  // Zadania: typ (tabela `task_types`), kontrahent i „zlecone przez" to odwołania bez resolvera
  // w `extRefLabels` — lista rysuje je tekstowymi etykietami z joinów (`taskListColumns.tsx`).
  tasks: ['task_type_id', 'contractor_id', 'created_by_user_id'],
};

/**
 * Jedna reguła „czy pole trafia na listę" — WSPÓLNA dla builderów kolumn (`buildCoreColumns`,
 * `buildExtColumns`) i palety kreatora („Dodaj kolumnę"). Gwarantuje: to, co paleta oferuje =
 * to, co lista umie narysować (koniec „dodajesz kolumnę → pokazuje się myślnik / nic").
 *
 * Brama: `showInList` (admin/ITE zdecydował, że pole może być na liście) + typ renderowalny:
 *  - `file` — brak renderera listy → poza listą (do dorobienia),
 *  - `reference` RDZENNE — tylko te z resolverem etykiety (`CORE_REFERENCE_SLUGS_WITH_LABEL`);
 *    referencja ext ma resolver zawsze (`extRefLabels`), więc przechodzi,
 *  - reszta typów renderuje się generykiem.
 *
 * `overrideSlugs` — slugi, dla których strona dostarcza WŁASNY renderer (rejestr nadpisań):
 * takie pole przechodzi bramę TYPU, mimo że generyk by je odsiał. Paleta (bez wiedzy o
 * nadpisaniach konkretnej strony) woła z setem z `PAGE_OVERRIDE_REFERENCE_SLUGS` — świadomie
 * zachowawczo (nie zaoferuje pola, którego nikt nie narysuje). UWAGA: `showInList` jest bramą
 * PIERWSZĄ — override jej NIE otwiera; pole z `showInList=false` zostaje poza listą MIMO
 * nadpisania (strażnik dev w `buildCoreColumns` to sygnalizuje).
 */
export function isFieldListable(
  field: SystemModuleFieldDef,
  opts?: { overrideSlugs?: ReadonlySet<string> },
): boolean {
  if (!field.showInList) return false;
  if (opts?.overrideSlugs?.has(field.slug)) return true;
  if (field.fieldType === 'file') return false;
  if (field.fieldType === 'reference') {
    return field.isCore ? CORE_REFERENCE_SLUGS_WITH_LABEL.has(field.slug) : true;
  }
  return true;
}

/**
 * Minimalny kształt funkcji tłumaczącej `t` używany przez nadpisania kolumn i funkcje etykiet kodów
 * (`*-select-labels`, `contractor-columns`, `ksef-jpk-markers`): z opcjonalnym `defaultValue` (surowy
 * kod spoza listy wraca jako tekst zastępczy). Runtime to pełny `TFunction` z `useTranslation`;
 * funkcja 1-argumentowa (jak `t` przekazywane do builderów) jest przypisywalna do tego typu.
 * JEDNO miejsce — nie definiuj lokalnych kopii.
 */
export type CoreColumnTranslateFn = (key: string, options?: { defaultValue?: string }) => string;

/** Kontekst wywołania nadpisania kolumny CORE — patrz `CoreColumnOverride`. */
export interface CoreColumnOverrideCtx {
  /** Definicja pola z katalogu (typ, nazwa, opcje, flagi). */
  field: SystemModuleFieldDef;
  /** Klucz camelCase w wierszu odpowiedzi (wyliczony ze sluga lub z `coreFieldMap`). */
  accessor: string;
  /**
   * `t` z i18next — z opcjonalnym `defaultValue` (nadpisania czasem mapują surowe kody na etykiety
   * z fallbackiem, np. typ dokumentu FA(3)); patrz `CoreColumnTranslateFn`.
   */
  t: CoreColumnTranslateFn;
  getRefLabels?: () => ExtRefLabels | undefined;
  /**
   * Serwerowa sortowalność tego pola (ta sama reguła co generyk) — override używa jej, by nie
   * plumbować `controls`/`sortableSlugs` ręcznie: `sortable: ctx.resolveSortable?.(ctx.field) ?? false`.
   */
  resolveSortable?: (field: SystemModuleFieldDef) => boolean;
  /**
   * Serwerowa filtrowalność tego pola (ta sama reguła co generyk — `resolveFilterable` z
   * `buildCoreColumns`): override dokłada filtr nagłówka tylko wtedy, gdy
   * `isFieldFilterable(ctx.field, ctx.resolveFilterable)` — serwer odrzuca filtr po polu spoza swojej
   * listy (`meta.filterable`); bez tej funkcji rozstrzyga flaga katalogu `field.isFilterable`.
   */
  resolveFilterable?: (field: SystemModuleFieldDef) => boolean;
}

/**
 * Nadpisanie renderu kolumny CORE per slug — dla pól rdzennych, których generyczny render
 * (typ→komórka) nie odtwarza wiernie: kwota+waluta, dostawca/nabywca/nadawca+NIP, status
 * jako plakietka, źródło jako badge, specjały umów (numer+flaga, okres, wartość+maskowanie).
 * Zwraca gotowy `ColumnDefinition`; `buildCoreColumns` wywoła go zamiast generyka. Rejestr
 * jest PER-KARTOTEKA (każda lista wnosi własny zestaw) — patrz plan (rejestr per-kartoteka).
 */
export type CoreColumnOverride<Row> = (ctx: CoreColumnOverrideCtx) => ColumnDefinition<Row>;

/**
 * Czy filtr nagłówka pola jest dostępny — JEDNA reguła dla generyka (`buildCoreColumns`) i dla
 * każdego nadpisania z filtrem: `resolveFilterable` podany → prawda z serwera (`meta.filterable`
 * ostatniej odpowiedzi listy); pominięty → flaga katalogu `field.isFilterable` (strony jeszcze
 * niewpięte). Nadpisanie, które rysuje filtr po swojemu, MUSI go dołożyć tylko wtedy, gdy ta funkcja
 * zwraca `true` — inaczej serwer odrzuciłby zapytanie z filtrem po polu spoza swojej listy.
 */
export function isFieldFilterable(
  field: SystemModuleFieldDef,
  resolveFilterable?: (field: SystemModuleFieldDef) => boolean,
): boolean {
  return resolveFilterable ? resolveFilterable(field) : field.isFilterable;
}

/**
 * Opcje listy wyboru filtra z zamkniętego zbioru kodów: wartość = kod (to, co serwer trzyma w
 * kolumnie), etykieta z `labelOf` — TEJ SAMEJ funkcji, z której korzysta komórka kolumny, więc filtr i
 * wiersze mówią to samo. JEDNO miejsce mapowania „kody → opcje filtra" dla wszystkich nadpisań.
 */
export function codeFilterOptions(
  codes: ReadonlyArray<string>,
  labelOf: (code: string, t: CoreColumnTranslateFn) => string,
  t: CoreColumnTranslateFn,
): ListFilterOption[] {
  return codes.map((code) => ({ value: code, label: labelOf(code, t) }));
}

/**
 * Nadpisanie kolumny CORE typu lista wyboru ze STAŁYM zbiorem kodów (zamknięty enum zapisany w
 * kodzie, nie słownik tenanta): status/pilność dokumentu, typ podmiotu i status VAT kontrahenta…
 * Generyk (`buildCoreColumns`) takiego pola nie umie obsłużyć — bez słownika nie ma skąd wziąć opcji,
 * więc kolumna dostawała w wierszu filtrów PUSTĄ komórkę, a w komórce surowy kod (`draft`).
 *
 * `labelOf` to JEDYNE miejsce mapowania kod → tekst: używa go komórka (wartość → etykieta) ORAZ
 * opcje listy wyboru filtra (kod → etykieta), więc to, co użytkownik wybiera w filtrze, jest
 * dokładnie tym, co widzi w wierszach. Kod spoza `codes` (dane historyczne) komórka pokazuje
 * etykietą z `defaultValue` = surowy kod — `labelOf` musi go przekazywać jako `defaultValue`.
 *
 * Wartość filtra = kod (to, co serwer trzyma w kolumnie), `fieldSlug` = slug pola. Opcje mają
 * `kanbanFallback: false` — dołożenie ich nie zmienia domyślnego pola grupującego Kanbana.
 * Sortowalność i filtrowalność jak w generyku (`resolveSortable` / `resolveFilterable`): filtr
 * nagłówka dochodzi tylko wtedy, gdy serwer deklaruje pole jako filtrowalne (`meta.filterable`) —
 * bez deklaracji serwer odrzuciłby zapytanie z takim filtrem. Gdy wołający nie poda
 * `resolveFilterable`, rozstrzyga flaga katalogu `field.isFilterable` (`isFieldFilterable`, ta sama
 * reguła co generyk). Prosty filtr tekstowy wiersza (`filterable`) jest wyłączony zawsze — to pole ma
 * listę wyboru, nie pole „Szukaj…".
 */
export function buildStaticSelectOverride<Row>(def: {
  codes: ReadonlyArray<string>;
  labelOf: (code: string, t: CoreColumnTranslateFn) => string;
  /** Domyślnie `true`. */
  defaultVisible?: boolean;
  defaultWidth?: number;
  /** Klasy tekstu komórki (np. wygaszenie); domyślnie bez klas, jak generyczna komórka `select`. */
  cellClassName?: string;
}): CoreColumnOverride<Row> {
  const { codes, labelOf, defaultVisible = true, defaultWidth, cellClassName } = def;
  return ({ field, accessor, t, resolveSortable, resolveFilterable }) => ({
    id: field.slug,
    accessor,
    header: field.name,
    defaultVisible,
    defaultWidth,
    sortable: resolveSortable?.(field) ?? false,
    filterable: false,
    ...(isFieldFilterable(field, resolveFilterable)
      ? {
          headerFilter: {
            category: 'select' as const,
            fieldSlug: field.slug,
            fieldType: 'select',
            options: codeFilterOptions(codes, labelOf, t),
            kanbanFallback: false,
          },
        }
      : {}),
    renderCell: (value) =>
      value === null || value === undefined || value === '' ? (
        <span className="text-text-tertiary">—</span>
      ) : (
        <span className={cellClassName}>{labelOf(String(value), t)}</span>
      ),
  });
}

// Dev-only strażniki (deduplikowane po slugu) — łapią ciche pułapki wzorca, których tsc nie widzi.
// Milczą w produkcji (`import.meta.env.DEV`).
const warnedMissingCoreKey = new Set<string>();
const warnedOverrideHidden = new Set<string>();

/**
 * [C] Strażnik mechanicznego klucza: gdy widoczna kolumna rdzenna nie ma swojego `accessor`
 * w wierszu odpowiedzi, `slug→camelCase` rozjeżdża się z endpointem — kolumna pokazałaby CICHY
 * myślnik. W dev ostrzegamy raz na slug z podpowiedzią naprawy (coreFieldMap / excludeSlugs).
 */
/**
 * [F] Strażnik dryfu mapy palety: strona nadpisała pole-referencję rdzenną, której sluga
 * nie ma w `PAGE_OVERRIDE_REFERENCE_SLUGS` — lista ją narysuje, ale paleta „Dodaj kolumnę"
 * jej nie zaoferuje (usunięcie kolumny w kreatorze = nieodwracalne z UI). Mapa jest
 * spłaszczana (bez klucza modułu — builder kolumn nie zna sluga modułu), więc strażnik
 * może przepuścić kolizję slugów między modułami; wystarcza jako sygnał dev.
 */
const PALETTE_MAPPED_REF_SLUGS: ReadonlySet<string> = new Set(
  Object.values(PAGE_OVERRIDE_REFERENCE_SLUGS).flat(),
);
const warnedRefOverrideNotInMap = new Set<string>();
function devWarnOverrideRefSlugNotInPaletteMap(field: SystemModuleFieldDef): void {
  if (!import.meta.env.DEV || field.fieldType !== 'reference' || !field.isCore) return;
  if (PALETTE_MAPPED_REF_SLUGS.has(field.slug) || warnedRefOverrideNotInMap.has(field.slug)) return;
  warnedRefOverrideNotInMap.add(field.slug);
  console.warn(
    `[buildCoreColumns] Nadpisanie pola-referencji rdzennej „${field.slug}" nie ma wpisu ` +
      `w PAGE_OVERRIDE_REFERENCE_SLUGS — paleta „Dodaj kolumnę" nie zaoferuje tej kolumny, ` +
      `więc jej usunięcie w Kreatorze Listy będzie nieodwracalne z UI. Dopisz slug do mapy.`,
  );
}

function devWarnMissingCoreKey(slug: string, accessor: string, row: unknown): void {
  if (!import.meta.env.DEV || warnedMissingCoreKey.has(slug)) return;
  if (row && typeof row === 'object' && !(accessor in row)) {
    warnedMissingCoreKey.add(slug);
    console.warn(
      `[buildCoreColumns] Kolumna rdzenna „${slug}" nie ma klucza „${accessor}" w wierszu ` +
        `odpowiedzi — mechaniczne slug→camelCase nie zgadza się z endpointem (cichy myślnik). ` +
        `Dodaj wpis do coreFieldMap ({ '${slug}': '<klucz>' }) albo obsłuż pole przez ` +
        `excludeSlugs + dedykowany builder.`,
    );
  }
}

/**
 * Kolumny pól RDZENNYCH (`isCore=true`) generowane z katalogu — bliźniak `buildExtColumns`
 * dla pól core. `accessor` (klucz w wierszu odpowiedzi) jest WYLICZANY ze sluga mechanicznie
 * (`slugToCamel`; np. `ksef_number` → `ksefNumber`) — wszystkie listy chodzą po endpointach
 * z mechanicznym camelCase, więc mapa per strona jest zbędna. `coreFieldMap` zostaje jako
 * OPCJONALNA furtka na ewentualny rozjazd (dziś zero wpisów). Render wg typu z JEDNEGO źródła
 * współdzielonego z ext (`renderExtFieldValue`/`renderBooleanCell`/`renderCoreReferenceCell`);
 * pola z wpisem w `overrides` dostają render bogaty (per-kartoteka).
 *
 * Dostępność pola rozstrzyga wspólna reguła `isFieldListable` (showInList + typ renderowalny) —
 * ta sama dla ext i palety kreatora. Dodatkowo pomijane są slugi z `excludeSlugs` (obsługiwane
 * osobno — firma/opiekun/kategoria dedykowanymi budowniczymi z record-select filtrami).
 * `id` kolumny = slug (to samo trafia do `list_config.columns` i do `?columns=`).
 *
 * Sortowalność: `multiselect`/`reference` NIEsortowalne — serwer nie sortuje po tablicy/uuid
 * (naprawa dawnego buga `sortable:true` na tych typach); poza tym `resolveSortable` (serwerowa
 * sortowalność listy) rozstrzyga per pole.
 */
export function buildCoreColumns<Row>(args: {
  fields: SystemModuleFieldDef[];
  t: (key: string) => string;
  /**
   * OPCJONALNE nadpisanie klucza odpowiedzi dla wybranych slugów (slug → klucz camelCase).
   * Domyślnie klucz jest WYLICZANY ze sluga (`slugToCamel`); mapa to furtka na ewentualny
   * rozjazd endpointu (dziś zero wpisów).
   */
  coreFieldMap?: Record<string, string>;
  getRefLabels?: () => ExtRefLabels | undefined;
  overrides?: Readonly<Record<string, CoreColumnOverride<Row>>>;
  /**
   * Slugi obsługiwane OSOBNO, dedykowanym budowniczym poza tym helperem (firma/opiekun/
   * kategoria — record-select filtry, których generyk nie odtworzy). Pomijane tutaj, żeby nie
   * zdublować kolumny.
   */
  excludeSlugs?: ReadonlySet<string>;
  /**
   * Rozstrzyga sortowalność kolumny rdzennej — wpięcie serwerowej sortowalności listy
   * (`isCoreColumnSortable` z `useServerListControls`, na bazie `meta.sortable` z ostatniej
   * odpowiedzi). Wołane per pole; gdy pominięte, builder używa domyślnej reguły typu.
   * NIEZALEŻNIE od wyniku, multiselect/reference są ZAWSZE niesortowalne (serwer nie sortuje
   * po tablicy/uuid) — resolver nie może ich „włączyć".
   */
  resolveSortable?: (field: SystemModuleFieldDef) => boolean;
  /**
   * Rozstrzyga filtrowalność kolumny rdzennej — wpięcie serwerowej filtrowalności listy
   * (`isCoreColumnFilterable` z `useServerListControls`, na bazie `meta.filterable` z ostatniej
   * odpowiedzi, analogicznie do `resolveSortable`). Wołane per pole; gdy POMINIĘTE, builder używa
   * dawnej reguły — flagi katalogu `f.isFilterable` (fallback: strony jeszcze niewpięte oraz
   * worktree firm współdzielący ten builder nie zmieniają zachowania). Bramka typu
   * (`filterCategory`) nadal wybiera widżet i odsiewa typy `none`.
   */
  resolveFilterable?: (field: SystemModuleFieldDef) => boolean;
  /**
   * Opcje listy wyboru pól `select` rdzenia wg sluga pola — pozycje słownika wczytane przez wołającego
   * (`useListFilterOptions`), bo ten builder jest czysty i sam ich nie pobierze. Używane TYLKO dla
   * pól bez własnych opcji w katalogu (`options` puste), które mają słownik (`dictionaryCode`):
   * dostają wtedy w wierszu filtrów listę wyboru (wartość = kod pozycji, etykieta ze słownika), a
   * komórka pokazuje etykietę zamiast surowego kodu. Brak wpisu (słownik jeszcze się nie wczytał,
   * brak dostępu) = jak dotąd: pusta komórka filtra i surowy kod. Nadpisania stron (`overrides`)
   * rysują się po swojemu — ich opcje buduje `buildStaticSelectOverride` / własny kod.
   */
  selectOptions?: CoreSelectFilterOptions;
  /**
   * OPCJONALNY tekst zastępczy dla odwołań nierozwiązywalnych (brak klucza w `extRefLabels`) —
   * zamiast surowego id (patrz `resolveReferenceLabel`). Domyślnie wyłączony: zwykłe listy
   * kartotek zachowują surowe id. Dotyczy generyka (core-referencje z resolverem); nadpisania
   * stron (`overrides`) rysują się po swojemu.
   */
  unresolvedRefLabel?: string;
}): ColumnDefinition<Row>[] {
  const {
    fields,
    t,
    getRefLabels,
    coreFieldMap = {},
    overrides = {},
    excludeSlugs,
    resolveSortable,
    resolveFilterable,
    selectOptions,
    unresolvedRefLabel,
  } = args;
  const overrideSlugs = new Set(Object.keys(overrides));
  // [E] Dev: override dla pola z showInList=false NIE pokaże kolumny (brama showInList jest
  // sprawdzana przed override w `isFieldListable`) — ostrzeż, bo to cichy footgun kopii.
  if (import.meta.env.DEV) {
    for (const slug of overrideSlugs) {
      const f = fields.find((x) => x.slug === slug);
      if (f && !f.showInList && !warnedOverrideHidden.has(slug)) {
        warnedOverrideHidden.add(slug);
        console.warn(
          `[buildCoreColumns] Nadpisanie dla „${slug}" zignorowane — pole ma showInList=false ` +
            `(brama showInList jest przed override).`,
        );
      }
    }
  }
  return (
    fields
      .filter((f) => f.isCore)
      // Slugi obsługiwane osobno (dedykowany builder) — nie dubluj kolumny.
      .filter((f) => !(excludeSlugs?.has(f.slug) ?? false))
      // Jedna reguła „co jest listowalne" (showInList + typ renderowalny) — wspólna z
      // `buildExtColumns` i paletą kreatora. Override może włączyć pole normalnie odsiane
      // (plik / referencja bez tłumacza), bo strona dostarcza własny renderer.
      .filter((f) => isFieldListable(f, { overrideSlugs }))
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((f): ColumnDefinition<Row> => {
        // Klucz odpowiedzi: wyliczany ze sluga (mechaniczny camelCase); mapa tylko dla wyjątków.
        const accessor = coreFieldMap[f.slug] ?? slugToCamel(f.slug);
        const override = overrides[f.slug];
        if (override) {
          // [F] Dryf mapy palety — patrz devWarnOverrideRefSlugNotInPaletteMap.
          devWarnOverrideRefSlugNotInPaletteMap(f);
          return override({
            field: f,
            accessor,
            t,
            getRefLabels,
            resolveSortable,
            resolveFilterable,
          });
        }

        const filterCategory = mapExtFieldTypeToFilterCategory(f.fieldType);
        // multiselect/reference: serwer nie sortuje po tablicy/uuid — nie dawaj uchwytu sortu.
        // Twarda bramka typu ma pierwszeństwo nad `resolveSortable` (nie da się ich „włączyć").
        const typeSortable = f.fieldType !== 'multiselect' && f.fieldType !== 'reference';
        const isSortable = typeSortable && (resolveSortable ? resolveSortable(f) : true);
        // Filtrowalność serwerowa (mirror sortu): `resolveFilterable` podany → prawda z serwera
        // (`meta.filterable`); pominięty → fallback na flagę katalogu `f.isFilterable` (stare
        // strony / worktree firm). Bramka typu `filterCategory` niżej dalej wybiera widżet.
        const isFilterable = isFieldFilterable(f, resolveFilterable);
        // Pole `select` bez własnych opcji w katalogu, ale z wczytanym słownikiem: lista wyboru
        // filtra i etykiety w komórce idą z TEGO SAMEGO zbioru (patrz `selectOptions`). Puste
        // `loadedSelectOptions` = brak słownika / jeszcze nie wczytany → zachowanie sprzed zmiany.
        const loadedSelectOptions =
          filterCategory === 'select' && (f.options?.length ?? 0) === 0
            ? selectOptions?.[f.slug]
            : undefined;
        const hasLoadedSelectOptions =
          loadedSelectOptions !== undefined && loadedSelectOptions.length > 0;
        const fieldForRender: SystemModuleFieldDef = hasLoadedSelectOptions
          ? { ...f, options: loadedSelectOptions.map((opt) => ({ ...opt })) }
          : f;
        // Core `reference` (jednowartościowy FK) → renderCoreReferenceCell (fallback surowe id;
        // firma dostaje własny override buildCompanyColumn z fallbackiem „—"). Reszta typów →
        // wspólny renderExtFieldValue (data/select/multiselect/boolean/tekst), czytając wartość
        // core wprost (nie z worka `row.ext`).
        const baseRender =
          f.fieldType === 'reference'
            ? renderCoreReferenceCell(f.slug, getRefLabels, t, 'id', unresolvedRefLabel)
            : (value: unknown) =>
                renderExtFieldValue(
                  value,
                  fieldForRender,
                  t,
                  getRefLabels?.()?.[f.slug],
                  unresolvedRefLabel,
                );
        // [C] W dev ostrzeż, gdy mechaniczny `accessor` nie istnieje w wierszu (rozjazd klucza).
        const renderCell = (value: unknown, row: Row) => {
          devWarnMissingCoreKey(f.slug, accessor, row);
          return baseRender(value);
        };
        return {
          id: f.slug,
          accessor,
          header: f.name,
          // Dostępne (przeszło bramę `showInList`) = domyślnie widoczne. Brak układu → wszystko
          // dostępne na ekranie; układ może potem ukryć.
          defaultVisible: true,
          sortable: isSortable,
          // Hint komparatora lokalnego — numeric z projekcji bywa stringiem (PG `numeric`).
          dataType:
            f.fieldType === 'integer' || f.fieldType === 'numeric'
              ? ('number' as const)
              : undefined,
          renderCell,
          // Prosty filter-row (input „Szukaj…", operator `zawiera`) TYLKO dla filtrowalnych
          // pól tekstowych — mirror `buildExtColumns` (typy nietekstowe filtrują się przez popup).
          filterable: isFilterable && filterCategory === 'text',
          ...(isFilterable && filterCategory !== 'none'
            ? {
                headerFilter: {
                  category: filterCategory,
                  fieldSlug: f.slug,
                  fieldType: f.fieldType,
                  ...(filterCategory === 'select' && f.options
                    ? {
                        options: f.options.map((opt) => ({
                          value: opt.value,
                          label: opt.label,
                          color: opt.color ?? undefined,
                        })),
                      }
                    : {}),
                  // Opcje ze słownika (wczytane przez wołającego) — tylko dla pól bez opcji w
                  // katalogu; `kanbanFallback: false`, bo opcje dołożono dla wiersza filtrów, a nie
                  // po to, żeby zmienić pole grupujące Kanbana.
                  ...(hasLoadedSelectOptions
                    ? {
                        options: loadedSelectOptions.map((opt) => ({ ...opt })),
                        kanbanFallback: false as const,
                      }
                    : {}),
                },
              }
            : {}),
        };
      })
  );
}

/**
 * Kolumny pól dodatkowych (ext, `isCore=false`) generowane z katalogu pól modułu —
 * etykieta bierze się z NAZWY pola (dane), nie ze statycznego klucza i18n (pola ext są
 * tworzone dynamicznie przez admina/ITE w kreatorze, więc nie mają tłumaczenia).
 * `id` kolumny = slug pola — to samo id trafia do `list_config.columns` (widoczność/
 * kolejność z Kreatora Listy) i do `?columns=` wysyłanego w zapytaniu listy.
 *
 * `Row` musi nieść opcjonalny `ext: {slug: wartość}` (backend dokleja go w trybie
 * projekcji `?columns=`).
 *
 * `getRefLabels` — opcjonalny getter `ExtRefLabels` (patrz `useExtRefLabelsGetter`),
 * przekazywany dalej do `renderExtFieldValue` dla pól `reference`. Wołany PRZY
 * KAŻDYM renderze komórki (nie przy budowie kolumn) — brak = kolumny `reference`
 * renderują surowe id (zachowanie sprzed wątku etykiet).
 *
 * `unresolvedRefLabel` — opcjonalny tekst zastępczy dla odwołań nierozwiązywalnych (brak klucza
 * w `extRefLabels`) zamiast surowego id; domyślnie wyłączony (patrz `resolveReferenceLabel`).
 */
export function buildExtColumns<Row extends { ext?: Record<string, unknown> }>(
  fields: SystemModuleFieldDef[],
  t: (key: string) => string,
  getRefLabels?: () => ExtRefLabels | undefined,
  unresolvedRefLabel?: string,
): ColumnDefinition<Row>[] {
  return (
    fields
      // Jedna reguła „listowalne" — wspólna z `buildCoreColumns` i paletą (showInList + typ
      // renderowalny). Dla ext odsiewa `file` (brak renderera); referencje ext przechodzą
      // (resolver etykiety `extRefLabels`).
      .filter((f) => !f.isCore && isFieldListable(f))
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((f) => {
        const filterCategory = mapExtFieldTypeToFilterCategory(f.fieldType);
        return {
          id: f.slug,
          accessor: (row: Row) => row.ext?.[f.slug],
          header: f.name,
          // Dostępne = domyślnie widoczne (brak układu → widać wszystko dostępne).
          defaultVisible: true,
          sortable: true,
          // Hint dla lokalnego komparatora sortowania (compareCellValues) — wartości
          // liczbowe z ext-tabeli bywają zwracane jako string (Postgres `numeric`).
          dataType:
            f.fieldType === 'integer' || f.fieldType === 'numeric'
              ? ('number' as const)
              : undefined,
          renderCell: (value: unknown) =>
            renderExtFieldValue(value, f, t, getRefLabels?.()?.[f.slug], unresolvedRefLabel),
          // Prosty wiersz filtrujący (input „Szukaj…", operator `zawiera`) — WYŁĄCZNIE dla
          // filtrowalnych pól tekstowych. Nietekstowe (data/liczba/select) filtrują się
          // przez popup nagłówka, logiczne przez trójstan (gałąź `isBooleanFilter` renderuje
          // się PRZED flagą `filterable`, więc `false` jej nie wyłącza); bez tej reguły
          // ConfigurableTable domyślnie (`filterable !== false`) rysował martwy input,
          // którego `zawiera` serwer i tak pomija bramką typu.
          filterable: f.isFilterable && filterCategory === 'text',
          // Filtr serwerowy w nagłówku kolumny — TYLKO gdy pole jest oznaczone jako
          // filtrowalne (admin/kreator, `isFilterable`) i mapa kategorii nie zwróciła
          // `'none'` (multiselect, reference — MVP bez filtra, patrz plan). `fieldSlug`
          // ustawiany JAWNIE — popup, pasek filtrów i pobieranie podpowiedzi czytają
          // `fieldSlug` bez fallbacku do `columnId`. Dla `select` dokładamy `options`,
          // inaczej popup pokazuje surowe zapisane kody zamiast etykiet/kolorów.
          ...(f.isFilterable && filterCategory !== 'none'
            ? {
                headerFilter: {
                  category: filterCategory,
                  fieldSlug: f.slug,
                  fieldType: f.fieldType,
                  // `color` w `SystemModuleFieldDef.options` dopuszcza `null` (kolumna
                  // DB), `ColumnHeaderFilterConfig.options` chce `string | undefined` —
                  // normalizacja przy przekazaniu.
                  ...(filterCategory === 'select' && f.options
                    ? {
                        options: f.options.map((opt) => ({
                          value: opt.value,
                          label: opt.label,
                          color: opt.color ?? undefined,
                        })),
                      }
                    : {}),
                },
              }
            : {}),
        };
      })
  );
}

/**
 * Reguła towarzysza dla `?columns=` (patrz `computeVisibleColumnParams`): gdy widoczna jest kolumna
 * ze slugiem `whenSlug`, do projekcji dochodzi też `addSlug` (np. kolumna złożona czyta dodatkowe pola).
 */
export interface ColumnCompanionRule {
  readonly whenSlug: string;
  readonly addSlug: string;
}

/**
 * Kategorie kolumn, których filtr da się ustawić WYŁĄCZNIE wyborem wartości (lista) albo wcale —
 * okno przy nagłówku ma zakładkę „Warunek” / „Zakres” tylko dla tekstu, liczby i daty
 * (`ColumnHeaderFilterPopup`). Warunek albo zakres w stanie takiej kolumny to pozostałość po jej
 * wcześniejszej kontrolce tekstowej (np. słownik wczytany po awarii zamienia pole tekstowe na
 * listę): byłby niewidoczny, a dalej zawężałby listę — nie idzie do serwera.
 */
const VALUES_ONLY_FILTER_CATEGORIES: ReadonlySet<string> = new Set([
  'select',
  'record-select',
  'none',
]);

/** Argumenty `buildListHeaderFilters` / `serializeListFilters`. */
export interface ListFilterSources {
  columns: ReadonlyArray<{ id: string; headerFilter?: { fieldSlug?: string; category?: string } }>;
  headerFilters: ColumnHeaderFilters;
  columnFilters: ColumnFilters;
  quickFilterConditions?: ReadonlyArray<{ field: string; operator: string; value?: unknown }>;
}

/**
 * Filtry listy do serwera jako JEDNA tablica `headerFilters` — wspólna dla stron list (faktury
 * zakupu, kartoteki własne) i sekcji kokpitu decyzji. Łączy trzy źródła, w tej kolejności:
 *  1. popup w nagłówku kolumny (`headerFilters`) — pole filtra z `headerFilter.fieldSlug` kolumny
 *     (fallback: id kolumny); warunek / zakres na kolumnie z samym wyborem wartości jest pomijany
 *     (`VALUES_ONLY_FILTER_CATEGORIES` — niewidoczna pozostałość po kontrolce tekstowej),
 *  2. wiersz „Szukaj…" pod nagłówkami (`columnFilters`, wartości już po debounce wołającego) →
 *     warunek „zawiera"; kolumna bez `headerFilter.fieldSlug` jest pomijana (wpisana wartość
 *     nigdzie by nie poleciała),
 *  3. opcjonalnie warunki aktywnego szybkiego filtra (`quickFilterConditions`, pole = id kolumny →
 *     `fieldSlug` jak wyżej; warunek bez kolumny / bez `fieldSlug` pomijany).
 * Wołający, który dokłada własne warunki (np. filtry stałe z pozycji menu), dopisuje je do wyniku.
 */
export function buildListHeaderFilters(args: ListFilterSources): ColumnHeaderFilter[] {
  const { columns, headerFilters, columnFilters, quickFilterConditions = [] } = args;
  const merged: ColumnHeaderFilter[] = [];

  for (const [columnId, filter] of Object.entries(headerFilters)) {
    const col = columns.find((c) => c.id === columnId);
    const category = col?.headerFilter?.category;
    if (
      category !== undefined &&
      VALUES_ONLY_FILTER_CATEGORIES.has(category) &&
      filter.mode !== 'values'
    ) {
      continue;
    }
    const fieldSlug = col?.headerFilter?.fieldSlug ?? columnId;
    if (fieldSlug) merged.push({ ...filter, field: fieldSlug });
  }

  for (const [columnId, val] of Object.entries(columnFilters)) {
    if (val == null || val === '') continue;
    const col = columns.find((c) => c.id === columnId);
    const fieldSlug = col?.headerFilter?.fieldSlug;
    if (!fieldSlug) continue;
    merged.push({
      field: fieldSlug,
      mode: 'condition',
      operator: 'contains',
      conditionValue: String(val),
    });
  }

  for (const cond of quickFilterConditions) {
    const col = columns.find((c) => c.id === cond.field);
    const fieldSlug = col?.headerFilter?.fieldSlug;
    if (!fieldSlug) continue;
    merged.push({
      field: fieldSlug,
      mode: 'condition',
      operator: cond.operator,
      conditionValue: cond.value == null ? '' : String(cond.value),
    });
  }

  return merged;
}

/**
 * Filtry listy do serwera jako JEDNA tablica `headerFilters` w postaci JSON (parametr zapytania) —
 * źródła i ich kolejność jak w `buildListHeaderFilters`. Brak filtrów → `undefined` (parametru nie
 * wysyłamy).
 */
export function serializeListFilters(args: ListFilterSources): string | undefined {
  const merged = buildListHeaderFilters(args);
  return merged.length > 0 ? JSON.stringify(merged) : undefined;
}

/**
 * Odwołania do pól z konfiguracji kalendarza (start, koniec, status, etykieta — w tej kolejności,
 * bez niewskazanych). Wartości są zapisane tak, jak ustawił je Kreator Listy: id kolumny albo slug
 * pola z katalogu — rozstrzyga je `computeVisibleColumnParams`.
 */
function calendarFieldRefs(calendarConfig: CalendarConfig): string[] {
  return [
    calendarConfig.startFieldSlug,
    calendarConfig.endFieldSlug,
    calendarConfig.statusFieldSlug,
    calendarConfig.labelFieldSlug,
  ].filter((ref): ref is string => ref !== undefined && ref !== '');
}

/**
 * Zbiór slugów aktualnie widocznych kolumn (dla `?columns=`) — algorytm lustrzany do
 * `initializeColumnConfigs` (useTableConfig.ts): wpis w układzie ma pierwszeństwo; brak wpisu →
 * kolumna UKRYTA gdy układ ISTNIEJE (nie wpychamy nowych „na chama"), `defaultVisible` gdy układu
 * BRAK (brak układu = widać wszystko dostępne). Kolumny core mapowane przez `coreIdToSlug`
 * (id camelCase → slug snake_case backendu), ext-slug wprost (id kolumny ext = slug).
 *
 * **Wyjątek — `hideable: false`.** Taka kolumna jest zawsze widoczna, NIEZALEŻNIE od tego, czy
 * w ogóle istnieje w zapisanym układzie — bo „nie da się ukryć" musi znaczyć „nigdy nie ukryta",
 * nie tylko „nie da się odznaczyć w kreatorze idąc od teraz". Bez tego stary układ zapisany
 * PRZED dodaniem takiej kolumny (np. przed migracją kartoteki na silnik list) chowa ją na zawsze
 * — user widzi listę bez kolumny identyfikującej wiersz (np. „Nazwa prawna" firmy, „Użytkownik"
 * przy Użytkownikach) i musi klikać każdy wiersz, żeby wiedzieć co to za rekord (2026-09-07).
 *
 * - `alwaysSlugs` — slugi dołączane BEZWARUNKOWO (zależności logiki strony niezależne od
 *   widoczności kolumny, np. `is_primary` steruje blokadą usuwania firmy). Podawane JAKO
 *   SLUGI (już rozwiązane), bez mapowania.
 * - `companions` — reguła towarzysza: gdy `whenSlug` jest w zbiorze widocznych, dołóż
 *   `addSlug` (np. podpis „krótka nazwa" renderowany pod nazwą, bez osobnej kolumny).
 * - `extraColumnIds` — dodatkowe ID KOLUMN (nie slugi — przechodzą przez TĘ SAMĄ regułę
 *   mapowania core/ext co główna lista `columns`, w odróżnieniu od `alwaysSlugs`)
 *   dołączane BEZWARUNKOWO, niezależnie od widoczności w `columns`/`savedLayoutColumns`.
 *   Przeznaczone dla konsumentów `ConfigurableTable.onVisibleColumnsChange` (ID kolumn z
 *   efektywnego zbioru widocznych, np. z innego renderu/kontekstu niż lokalna lista
 *   `columns` tej strony) — ID nierozpoznane w żadnej z map (core/ext) są cicho pomijane,
 *   identycznie jak w głównej pętli.
 * - `calendarConfig` — konfiguracja kalendarza układu (`list_config.calendarConfig`): slugi jego
 *   pól (start / koniec / status / etykieta) są dołączane BEZWARUNKOWO, niezależnie od widoczności
 *   kolumn i od tego, czy lista jest akurat w trybie kalendarza — kalendarz nie zależy od widocznych
 *   kolumn (`CalendarBoard` szuka pól wśród wszystkich definicji kolumn strony), więc wiersze muszą
 *   nieść ich wartości zawsze. Zapisana wartość bywa ID kolumny (układy z Kreatora sprzed katalogu
 *   pól: `company`, `period`) albo slugiem backendu z katalogu pól (`company_id`, `date_from`, slug
 *   pola dodatkowego); rozpoznajemy oba (mapa `coreIdToSlug`, jej wartości, `extSlugSet`).
 *   Wartość nierozpoznana (pole bez kolumny na tej stronie, np. `signed_date` umów) jest cicho
 *   pomijana — i tak nie ma kolumny, która by ją narysowała. Slugi pól dołączone tą drogą dostają
 *   też reguły towarzysza (`companions`), jak kolumny widoczne.
 */
export function computeVisibleColumnParams(args: {
  columns: ReadonlyArray<{ id: string; defaultVisible?: boolean; hideable?: boolean }>;
  savedLayoutColumns?: ReadonlyArray<{ id: string; visible?: boolean }>;
  coreIdToSlug: Readonly<Record<string, string>>;
  extSlugSet: ReadonlySet<string>;
  alwaysSlugs?: readonly string[];
  companions?: readonly ColumnCompanionRule[];
  extraColumnIds?: readonly string[];
  calendarConfig?: CalendarConfig;
}): string[] {
  const {
    columns,
    savedLayoutColumns,
    coreIdToSlug,
    extSlugSet,
    alwaysSlugs = [],
    companions = [],
    extraColumnIds = [],
    calendarConfig,
  } = args;
  const savedById = new Map((savedLayoutColumns ?? []).map((c) => [c.id, c]));
  // Jest zapisany układ → jest DOSŁOWNY: kolumna spoza niego jest ukryta (nie wpychamy nowych pól
  // „na chama"). Brak układu → `defaultVisible` (brak układu = widać wszystko dostępne). Lustro
  // `initializeColumnConfigs` (useTableConfig.ts). Wyjątek: `hideable === false` wygrywa zawsze
  // (patrz komentarz nad funkcją).
  const hasLayout = (savedLayoutColumns?.length ?? 0) > 0;
  const slugs: string[] = [];
  for (const col of columns) {
    const saved = savedById.get(col.id);
    const visible =
      col.hideable === false
        ? true
        : saved
          ? (saved.visible ?? col.defaultVisible ?? true)
          : hasLayout
            ? false
            : (col.defaultVisible ?? true);
    if (!visible) continue;
    const coreSlug = coreIdToSlug[col.id];
    if (coreSlug) {
      slugs.push(coreSlug);
    } else if (extSlugSet.has(col.id)) {
      slugs.push(col.id);
    }
  }
  for (const id of extraColumnIds) {
    const coreSlug = coreIdToSlug[id];
    if (coreSlug) {
      slugs.push(coreSlug);
    } else if (extSlugSet.has(id)) {
      slugs.push(id);
    }
  }
  if (calendarConfig) {
    // Slugi backendu znanych kolumn (wartości mapy) — zapisana wartość kalendarza może być już
    // slugiem z katalogu pól, którego mapa nie ma jako klucza (`company_id`: kolumna ma id `company`).
    const backendSlugs = new Set(Object.values(coreIdToSlug));
    for (const ref of calendarFieldRefs(calendarConfig)) {
      // `hasOwn` — wartość pochodzi z zapisanego układu, a nie z kodu: nazwa jak `constructor`
      // nie może trafić w prototyp mapy.
      const mapped = Object.hasOwn(coreIdToSlug, ref) ? coreIdToSlug[ref] : undefined;
      if (mapped) {
        slugs.push(mapped);
      } else if (backendSlugs.has(ref) || extSlugSet.has(ref)) {
        slugs.push(ref);
      }
    }
  }
  for (const s of alwaysSlugs) {
    if (!slugs.includes(s)) slugs.push(s);
  }
  for (const comp of companions) {
    if (slugs.includes(comp.whenSlug) && !slugs.includes(comp.addSlug)) {
      slugs.push(comp.addSlug);
    }
  }
  return Array.from(new Set(slugs));
}
